import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ClerkLoaded, ClerkLoading, useAuth, useClerk, useSignIn } from '@clerk/clerk-react';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/ui/loader';
import unifyLogo from '@/assets/unify-logo.png';

const GoogleGlyph = () => (
	<svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
		<path
			fill="#FFC107"
			d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
		/>
		<path
			fill="#FF3D00"
			d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"
		/>
		<path
			fill="#4CAF50"
			d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C9.6 39.5 16.2 44 24 44z"
		/>
		<path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.1 44 24c0-1.2-.1-2.3-.4-3.5z" />
	</svg>
);

const Backdrop = () => (
	<>
		<div className="absolute inset-0 bg-gradient-soft opacity-60 pointer-events-none" />
		<div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-gradient-unify opacity-20 blur-3xl pointer-events-none" />
		<div className="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-gradient-unify opacity-20 blur-3xl pointer-events-none" />
	</>
);

// Shown when a sign-in completes but Clerk returns a `pending` session — on this
// instance that means the account has no organisation to activate (e.g. an abandoned
// sign-up), so the app would treat the user as signed out and bounce them straight
// back to /login in a silent loop (NEA-5953).
const NO_STORE_MESSAGE = "This account isn't linked to a store yet, so we can't sign you in. Please contact support@unify.near.st.";

// `no_account` is Google-specific (only the OAuth `transferable`/`unverified` branch
// of SsoCallbackInner produces it). `failed` is the catch-all for any /login/sso-callback
// bounce-back, including the `email_link` flow that reuses the same callback route —
// keep the wording generic so we don't tell an email-link user that Google failed.
const SIGN_IN_ERROR_MESSAGES = {
	no_account: 'No Unify account uses that Google email. Please sign up first, or sign in with email and password.',
	no_store: NO_STORE_MESSAGE,
	failed: "Couldn't finish signing in. Please try again."
};

const primarySubmitButtonClasses =
	'w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-gradient-unify text-primary-foreground text-sm font-semibold shadow-[0_10px_24px_-12px_rgba(79,70,229,0.5)] hover:scale-[1.01] active:scale-[0.99] transition-transform disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100';

const SignInCard = () => {
	const { signIn, isLoaded, setActive } = useSignIn();
	const clerk = useClerk();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const initialSignInError = SIGN_IN_ERROR_MESSAGES[searchParams.get('signInError')] || null;

	// 'email' → discover supported factors; then 'password' / 'code' / 'second_factor_code' / 'link_sent' / 'reset' depending on what Clerk reports back.
	const [step, setStep] = useState('email');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const [newPassword, setNewPassword] = useState('');
	// Only set when the account exposes a password factor *and* a reset-by-email-code
	// factor — gates the "Forgot password?" affordance to password-login users.
	const [resetEmailAddressId, setResetEmailAddressId] = useState(null);
	const [error, setError] = useState(initialSignInError);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (searchParams.get('signInError')) {
			const next = new URLSearchParams(searchParams);
			next.delete('signInError');
			setSearchParams(next, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	const goBackToEmail = () => {
		setStep('email');
		setPassword('');
		setCode('');
		setNewPassword('');
		setResetEmailAddressId(null);
		setError(null);
	};

	// The Unify Clerk instance is in single-session mode, so starting a sign-in while this
	// browser already holds a session throws `session_exists` — the user is signed in via
	// another tab, or a previous sign-in left a 'pending' (org-less) session behind. Sign
	// that session out and retry once, so logging in on this screen wins (NEA-5953).
	const withStaleSessionRetry = async (startSignIn) => {
		try {
			return await startSignIn();
		} catch (err) {
			if (err.errors?.[0]?.code !== 'session_exists') throw err;
			await clerk.signOut();
			return startSignIn();
		}
	};

	const handleEmailContinue = async (e) => {
		e.preventDefault();
		if (!isLoaded) return;

		setError(null);
		setSubmitting(true);
		try {
			const attempt = await withStaleSessionRetry(() => signIn.create({ identifier: email }));
			const factors = attempt.supportedFirstFactors || [];

			if (factors.some((f) => f.strategy === 'password')) {
				// Only password-login accounts get the reset affordance — gate it on a
				// reset-by-email-code factor actually being offered alongside the password.
				const resetFactor = factors.find((f) => f.strategy === 'reset_password_email_code');
				setResetEmailAddressId(resetFactor?.emailAddressId || null);
				setStep('password');
				return;
			}

			const codeFactor = factors.find((f) => f.strategy === 'email_code');
			if (codeFactor) {
				await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: codeFactor.emailAddressId });
				setStep('code');
				return;
			}

			const linkFactor = factors.find((f) => f.strategy === 'email_link');
			if (linkFactor) {
				await signIn.prepareFirstFactor({
					strategy: 'email_link',
					emailAddressId: linkFactor.emailAddressId,
					redirectUrl: `${window.location.origin}/login/sso-callback`
				});
				setStep('link_sent');
				return;
			}

			setError("This account doesn't support email sign-in. Try 'Sign in with Google' instead.");
		} catch (err) {
			const errCode = err.errors?.[0]?.code;
			if (errCode === 'form_identifier_not_found') {
				setError('No Unify account uses that email. Please sign up first.');
			} else if (err.errors?.[0]?.message) {
				setError(err.errors[0].message);
			} else {
				setError('Something went wrong. Please try again.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	// setActive + redirect once Clerk reports the sign-in is finished. Shared by the first-factor,
	// second-factor and password-reset submit paths; returns true when it handled a completed
	// sign-in so callers can stop.
	const completeSignIn = async (result) => {
		if (result.status === 'complete') {
			await setActive({ session: result.createdSessionId });
			if (clerk.session?.status === 'pending') {
				await clerk.signOut();
				setError(NO_STORE_MESSAGE);
				return true;
			}
			navigate('/');
			return true;
		}
		return false;
	};

	const completeFirstFactor = async (strategy, params) => {
		setError(null);
		setSubmitting(true);
		try {
			const result = await signIn.attemptFirstFactor({ strategy, ...params });
			if (await completeSignIn(result)) return;

			// The production Unify Clerk instance asks for an email second factor once the password
			// (or first-factor code) verifies (NEA-5800). Pick the email-code factor and move to a
			// code-entry step; email_link / totp aren't handled here and fall through to the message below.
			if (result.status === 'needs_second_factor') {
				const factor = result.supportedSecondFactors?.find((f) => f.strategy === 'email_code');
				if (factor) {
					await signIn.prepareSecondFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
					setCode('');
					setStep('second_factor_code');
					return;
				}
			}

			setError('Additional verification is required to finish signing in.');
		} catch (err) {
			const errCode = err.errors?.[0]?.code;
			if (errCode === 'form_password_incorrect') {
				setError("That password doesn't match the account. Please try again.");
			} else if (errCode === 'form_code_incorrect') {
				setError("That code didn't match. Please try again.");
			} else if (err.errors?.[0]?.message) {
				setError(err.errors[0].message);
			} else {
				setError('Sign-in failed. Please try again.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handlePasswordSubmit = (e) => {
		e.preventDefault();
		completeFirstFactor('password', { password });
	};

	const handleCodeSubmit = (e) => {
		e.preventDefault();
		completeFirstFactor('email_code', { code });
	};

	const handleSecondFactorSubmit = async (e) => {
		e.preventDefault();
		if (!isLoaded) return;
		setError(null);
		setSubmitting(true);
		try {
			const result = await signIn.attemptSecondFactor({ strategy: 'email_code', code });
			if (await completeSignIn(result)) return;
			setError('Additional verification is required to finish signing in.');
		} catch (err) {
			const errCode = err.errors?.[0]?.code;
			if (errCode === 'form_code_incorrect') {
				setError("That code didn't match. Please try again.");
			} else if (err.errors?.[0]?.message) {
				setError(err.errors[0].message);
			} else {
				setError('Sign-in failed. Please try again.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleForgotPassword = async () => {
		if (!isLoaded || !resetEmailAddressId) return;
		setError(null);
		setSubmitting(true);
		try {
			await signIn.prepareFirstFactor({ strategy: 'reset_password_email_code', emailAddressId: resetEmailAddressId });
			setCode('');
			setNewPassword('');
			setStep('reset');
		} catch (err) {
			setError(err.errors?.[0]?.message || "Couldn't start the password reset. Please try again.");
		} finally {
			setSubmitting(false);
		}
	};

	const handleResetSubmit = async (e) => {
		e.preventDefault();
		if (!isLoaded) return;
		setError(null);
		setSubmitting(true);
		try {
			const result = await signIn.attemptFirstFactor({ strategy: 'reset_password_email_code', code, password: newPassword });
			if (await completeSignIn(result)) return;
			setError('Additional verification is required to finish resetting your password.');
		} catch (err) {
			const errCode = err.errors?.[0]?.code;
			if (errCode === 'form_code_incorrect') {
				setError("That code didn't match. Please try again.");
			} else if (errCode === 'form_password_pwned') {
				setError('That password has appeared in a data breach. Please choose a different one.');
			} else if (errCode === 'form_password_length_too_short') {
				setError('That password is too short. Please choose a longer one.');
			} else if (err.errors?.[0]?.message) {
				setError(err.errors[0].message);
			} else {
				setError("Couldn't reset your password. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleGoogleSignIn = async () => {
		if (!isLoaded) return;
		setError(null);
		try {
			await withStaleSessionRetry(() =>
				signIn.authenticateWithRedirect({
					strategy: 'oauth_google',
					redirectUrl: '/login/sso-callback',
					redirectUrlComplete: '/'
				})
			);
		} catch (err) {
			setError(err.errors?.[0]?.message || 'Could not start Google sign-in. Please try again.');
		}
	};

	return (
		<div className="rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
			<div className="px-5 pt-5 pb-4 text-center">
				<h1 className="text-2xl font-extrabold tracking-tight text-foreground">Sign in to Unify</h1>
				<p className="mt-1.5 text-sm text-muted-foreground">Welcome back! Please sign in to continue</p>
			</div>

			<div className="px-5 pb-5 space-y-4">
				<Button
					type="button"
					variant="outline"
					onClick={handleGoogleSignIn}
					disabled={!isLoaded || submitting}
					className="w-full h-11 gap-2.5 text-sm font-semibold"
				>
					<GoogleGlyph />
					Sign in with Google
				</Button>

				<div className="relative flex items-center">
					<div className="flex-grow border-t border-border" />
					<span className="px-3 text-xs text-muted-foreground">or</span>
					<div className="flex-grow border-t border-border" />
				</div>

				{step === 'email' ? (
					<form onSubmit={handleEmailContinue} className="space-y-4">
						<div>
							<label htmlFor="login-email" className="block text-sm font-bold text-foreground mb-1.5">
								Email address
							</label>
							<Input
								id="login-email"
								type="email"
								autoComplete="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="Enter your email address"
								required
							/>
						</div>

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<button type="submit" disabled={!isLoaded || submitting} className={primarySubmitButtonClasses}>
							{submitting ? 'Checking…' : 'Continue'} <ArrowRight className="h-4 w-4" />
						</button>
					</form>
				) : null}

				{step === 'password' ? (
					<form onSubmit={handlePasswordSubmit} className="space-y-4">
						<EmailRecap email={email} onChange={goBackToEmail} />
						<div>
							<div className="flex items-center justify-between mb-1.5">
								<label htmlFor="login-password" className="block text-sm font-bold text-foreground">
									Password
								</label>
								{resetEmailAddressId ? (
									<button
										type="button"
										onClick={handleForgotPassword}
										disabled={!isLoaded || submitting}
										className="text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-70"
									>
										Forgot password?
									</button>
								) : null}
							</div>
							<Input
								id="login-password"
								type="password"
								autoComplete="current-password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Enter your password"
								autoFocus
								required
							/>
						</div>

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<button type="submit" disabled={!isLoaded || submitting} className={primarySubmitButtonClasses}>
							{submitting ? 'Signing in…' : 'Sign in'} <ArrowRight className="h-4 w-4" />
						</button>
					</form>
				) : null}

				{step === 'code' ? (
					<form onSubmit={handleCodeSubmit} className="space-y-4">
						<EmailRecap email={email} onChange={goBackToEmail} />
						<div>
							<label htmlFor="login-code" className="block text-sm font-bold text-foreground mb-1.5">
								Sign-in code
							</label>
							<Input
								id="login-code"
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								value={code}
								onChange={(e) => setCode(e.target.value)}
								placeholder="6-digit code"
								autoFocus
								required
							/>
							<p className="mt-1.5 text-xs text-muted-foreground">We&apos;ve emailed a code to {email}.</p>
						</div>

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<button type="submit" disabled={!isLoaded || submitting} className={primarySubmitButtonClasses}>
							{submitting ? 'Verifying…' : 'Verify and sign in'} <ArrowRight className="h-4 w-4" />
						</button>
					</form>
				) : null}

				{step === 'second_factor_code' ? (
					<form onSubmit={handleSecondFactorSubmit} className="space-y-4">
						<EmailRecap email={email} onChange={goBackToEmail} />
						<div>
							<label htmlFor="login-2fa-code" className="block text-sm font-bold text-foreground mb-1.5">
								Verification code
							</label>
							<Input
								id="login-2fa-code"
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								value={code}
								onChange={(e) => setCode(e.target.value)}
								placeholder="6-digit code"
								autoFocus
								required
							/>
							<p className="mt-1.5 text-xs text-muted-foreground">
								For extra security, we&apos;ve emailed a verification code to {email}.
							</p>
						</div>

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<button type="submit" disabled={!isLoaded || submitting} className={primarySubmitButtonClasses}>
							{submitting ? 'Verifying…' : 'Verify and sign in'} <ArrowRight className="h-4 w-4" />
						</button>
					</form>
				) : null}

				{step === 'link_sent' ? (
					<div className="space-y-4">
						<EmailRecap email={email} onChange={goBackToEmail} />
						<p className="text-sm text-muted-foreground">
							We&apos;ve sent a sign-in link to <span className="font-semibold text-foreground">{email}</span>. Open it on
							this device to finish signing in.
						</p>
					</div>
				) : null}

				{step === 'reset' ? (
					<form onSubmit={handleResetSubmit} className="space-y-4">
						<EmailRecap email={email} onChange={goBackToEmail} />
						<div>
							<label htmlFor="reset-code" className="block text-sm font-bold text-foreground mb-1.5">
								Reset code
							</label>
							<Input
								id="reset-code"
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								value={code}
								onChange={(e) => setCode(e.target.value)}
								placeholder="6-digit code"
								autoFocus
								required
							/>
							<p className="mt-1.5 text-xs text-muted-foreground">We&apos;ve emailed a reset code to {email}.</p>
						</div>
						<div>
							<label htmlFor="reset-new-password" className="block text-sm font-bold text-foreground mb-1.5">
								New password
							</label>
							<Input
								id="reset-new-password"
								type="password"
								autoComplete="new-password"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								placeholder="Choose a new password"
								required
							/>
						</div>

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<button type="submit" disabled={!isLoaded || submitting} className={primarySubmitButtonClasses}>
							{submitting ? 'Resetting…' : 'Reset password and sign in'} <ArrowRight className="h-4 w-4" />
						</button>
					</form>
				) : null}
			</div>

			<div className="border-t border-border/60 bg-muted/30 px-5 py-3 text-center text-sm text-muted-foreground">
				Don&apos;t have an account?{' '}
				{/* Marketing pricing cards carry the ?plan= param that create-shop needs
				    (NEA-6203). Routing directly to /onboarding here drops it and the shop
				    lands with the `unknown-plan` sentinel. */}
				<a href="https://unify.near.st/pricing" className="font-semibold text-foreground hover:underline">
					Sign up
				</a>
			</div>
		</div>
	);
};

const EmailRecap = ({ email, onChange }) => (
	<div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
		<span className="truncate text-foreground">{email}</span>
		<button type="button" onClick={onChange} className="text-xs font-semibold text-primary hover:underline">
			Change
		</button>
	</div>
);

const SignedInRedirect = () => {
	const { isLoaded, isSignedIn } = useAuth();
	if (!isLoaded) return null;
	if (isSignedIn) return <Navigate to="/" replace />;
	return <SignInCard />;
};

export const Login = () => (
	<main className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
		<Backdrop />

		<div className="relative w-full max-w-[420px]">
			<div className="flex justify-center mb-8">
				<img src={unifyLogo} alt="Unify by NearSt" className="h-20 w-auto" />
			</div>

			<ClerkLoading>
				<div className="rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-10">
					<Loader label="Loading sign-in…" />
				</div>
			</ClerkLoading>
			<ClerkLoaded>
				<SignedInRedirect />
			</ClerkLoaded>

			<p className="mt-6 text-center text-xs text-muted-foreground">
				By using Unify you agree to our{' '}
				<a
					href="https://near.st/legal/retailer-terms/unify"
					target="_blank"
					rel="noreferrer"
					className="font-semibold text-foreground underline"
				>
					Terms &amp; Conditions
				</a>{' '}
				&amp;{' '}
				<a
					href="https://near.st/legal/general-site-privacy-policy"
					target="_blank"
					rel="noreferrer"
					className="font-semibold text-foreground underline"
				>
					Privacy Policy
				</a>
			</p>
		</div>
	</main>
);

/**
 * Custom OAuth callback. Unlike Clerk's built-in `<AuthenticateWithRedirectCallback>`,
 * we deliberately don't transfer an unmatched OAuth identity into a `signUp.create({ transfer: true })`
 * — that path created an orphan Clerk user with no org membership (Unify's single-org-per-user
 * constraint means orgs are only created via the onboarding flow). Sign-in is the only outcome
 * we accept here; anything else bounces back to `/login` with a friendly error.
 */
const SsoCallbackInner = () => {
	const { signIn, isLoaded, setActive } = useSignIn();
	const clerk = useClerk();
	const navigate = useNavigate();
	const [handled, setHandled] = useState(false);

	useEffect(() => {
		if (!isLoaded || handled) return;
		setHandled(true);

		const run = async () => {
			if (signIn?.status === 'complete' && signIn.createdSessionId) {
				await setActive({ session: signIn.createdSessionId });
				if (clerk.session?.status === 'pending') {
					await clerk.signOut();
					navigate('/login?signInError=no_store', { replace: true });
					return;
				}
				navigate('/', { replace: true });
				return;
			}

			const externalStatus = signIn?.firstFactorVerification?.status;
			if (externalStatus === 'transferable' || externalStatus === 'unverified') {
				navigate('/login?signInError=no_account', { replace: true });
				return;
			}

			navigate('/login?signInError=failed', { replace: true });
		};

		run().catch(() => navigate('/login?signInError=failed', { replace: true }));
	}, [isLoaded, handled, signIn, setActive, clerk, navigate]);

	return <Loader label="Finishing sign-in…" />;
};

export const LoginSsoCallback = () => (
	<main className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
		<Backdrop />
		<div className="relative">
			<ClerkLoading>
				<Loader label="Finishing sign-in…" />
			</ClerkLoading>
			<ClerkLoaded>
				<SsoCallbackInner />
			</ClerkLoaded>
		</div>
	</main>
);
